import Card from '@mui/material/Card';
import Stack from '@mui/material/Stack';
import Grid from '@mui/material/Unstable_Grid2';
import Typography from '@mui/material/Typography';
import {
  Button,
  Divider,
  Link,
  ListItemText,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';

import { RouterLink } from 'src/routes/components';

import { fDateTime } from 'src/utils/format-time';
import { TABLE_HEAD_CELL_TYPE } from 'src/utils/common-types';

import Scrollbar from 'src/components/scrollbar';
import { TableHeadCustom } from 'src/components/table';

import { IPackagingReturnDetailedItem } from 'src/types/packaging-return';

import { paths } from '../../routes/paths';

const LINE_ITEM_HEADERS: TABLE_HEAD_CELL_TYPE[] = [
  { id: 'itemCode', label: 'Item Code', align: 'left', primary: true },
  { id: 'itemName', label: 'Item Name', align: 'left', primary: true, minWidth: 220 },
  { id: 'product', label: 'Product', align: 'left', primary: true },
  { id: 'sourceDispatch', label: 'Source Dispatch', align: 'left', primary: true },
  { id: 'quantity', label: 'Quantity', align: 'left', primary: true },
  { id: 'issuedBy', label: 'Issued By', align: 'left', primary: true },
  { id: 'damaged', label: 'Damaged (Supplier / Staff)', align: 'left', primary: true },
];

type Props = {
  currentReturn: IPackagingReturnDetailedItem;
};

export default function PackagingReturnDetails({ currentReturn }: Props) {
  return (
    <Grid container spacing={3}>
      <Grid xs={12}>
        <Card sx={{ p: 3 }}>
          <Grid container spacing={2}>
            <Grid
              mb={1}
              display="flex"
              mt={2}
              alignItems="center"
              justifyContent="space-between"
              width={1}
            >
              <Typography variant="subtitle2">Basic Information</Typography>
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={currentReturn.returnCode}
                secondary="Return No"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={fDateTime(currentReturn.returnDate)}
                secondary="Return Date"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
              />
            </Grid>

            <Grid xs={12} md={4}>
              <ListItemText
                primary={currentReturn.items.length}
                secondary="Item Lines"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
              />
            </Grid>

            <Grid xs={12} md={6}>
              <ListItemText
                primary={`${currentReturn.receivedBy.firstName}${
                  currentReturn.receivedBy.lastName ? ` ${currentReturn.receivedBy.lastName}` : ''
                }`}
                secondary="Received By (Staff)"
                primaryTypographyProps={{ typography: 'body2' }}
                secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
              />
            </Grid>

            {currentReturn.remarks && (
              <Grid xs={12}>
                <ListItemText
                  primary={currentReturn.remarks}
                  secondary="Remarks"
                  primaryTypographyProps={{ typography: 'body2' }}
                  secondaryTypographyProps={{ component: 'span', color: 'text.disabled' }}
                />
              </Grid>
            )}

            <Divider
              sx={{
                width: 1,
                my: 2,
                borderStyle: 'dashed',
              }}
            />

            <Grid mb={1} xs={12} width="full">
              <Typography variant="subtitle2">Line Items</Typography>
            </Grid>

            <Grid xs={12}>
              <TableContainer sx={{ position: 'relative', overflow: 'hidden', height: '300px' }}>
                <Scrollbar>
                  <Table stickyHeader size="small" sx={{ minWidth: 780 }}>
                    <TableHeadCustom headLabel={LINE_ITEM_HEADERS} />

                    <TableBody>
                      {currentReturn.items.map((item) => (
                        <TableRow hover key={item._id}>
                          <TableCell align="left">
                            <Link
                              component={RouterLink}
                              href={paths.dashboard.packagingItem.view(item.packagingItem._id)}
                            >
                              {item.packagingItem.itemCode}
                            </Link>
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.packagingItem.itemName}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.product.productName}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            <Link
                              component={RouterLink}
                              href={paths.dashboard.packagingDispatch.view(
                                item.sourceDispatchItem.dispatch._id
                              )}
                            >
                              {item.sourceDispatchItem.dispatch.dispatchCode}
                            </Link>
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.quantity}
                          </TableCell>

                          <TableCell sx={{ whiteSpace: 'nowrap', textAlign: 'left' }}>
                            {item.issuedBy
                              ? `${item.issuedBy.firstName}${
                                  item.issuedBy.lastName ? ` ${item.issuedBy.lastName}` : ''
                                } (${item.issuedBy.employeeNumber})`
                              : '—'}
                          </TableCell>

                          <TableCell
                            sx={{
                              whiteSpace: 'nowrap',
                              textAlign: 'left',
                              color: item.damaged ? 'error.main' : 'inherit',
                            }}
                          >
                            {item.damaged
                              ? `${item.supplierDamagedQuantity ?? 0} / ${item.staffDamagedQuantity ?? 0}`
                              : '-'}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </Scrollbar>
              </TableContainer>
            </Grid>
          </Grid>

          <Stack direction="row" justifyContent="flex-end" gap={2} mt={3}>
            <Link component={RouterLink} href={paths.dashboard.packagingReturn.root}>
              <Button variant="outlined" color="inherit">
                Back
              </Button>
            </Link>
          </Stack>
        </Card>
      </Grid>
    </Grid>
  );
}
